import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, Button,
  LinearProgress, Avatar, Grow
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../config';
import { jsPDF } from 'jspdf';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PsychologyIcon from '@mui/icons-material/Psychology';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LoopIcon from '@mui/icons-material/Loop';

// ── jsPDF helper: wrap text inside a rect ──
function drawWrappedText(pdf, text, x, y, maxWidth, lineHeight, maxLines) {
  const lines = pdf.splitTextToSize(text, maxWidth);
  const limited = maxLines ? lines.slice(0, maxLines) : lines;
  pdf.text(limited, x, y);
  return y + limited.length * lineHeight;
}

function generatePdf(data, quoteIds) {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = 210;
  const margin = 16;
  const contentW = pageW - margin * 2;
  let y = margin;

  const { analysis, metrics } = data || {};

  // ── Header ──
  pdf.setFillColor(239, 246, 255);
  pdf.rect(0, 0, pageW, 32, 'F');
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 64, 175);
  pdf.setFontSize(18);
  pdf.text('AI Strategic Plan', margin, 16);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(59, 130, 246);
  pdf.text(`Historical performance analysis  •  ${quoteIds.length} quote${quoteIds.length !== 1 ? 's' : ''} analyzed`, margin, 24);
  y = 40;

  // ── Section helper ──
  const sectionTitle = (title, color) => {
    pdf.setFillColor(248, 250, 252);
    pdf.rect(margin, y - 4, contentW, 8, 'F');
    pdf.setDrawColor(226, 232, 240);
    pdf.setLineWidth(0.3);
    pdf.line(margin, y + 4, pageW - margin, y + 4);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.setTextColor(color[0], color[1], color[2]);
    pdf.text(title, margin, y + 1.5);
    y += 12;
  };

  const bulletItem = (text) => {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(51, 65, 85);
    pdf.text('\u2022', margin + 2, y);
    y = drawWrappedText(pdf, text, margin + 7, y, contentW - 9, 5, 20);
    y += 2;
  };

  // ════════ WIN RATE ASSESSMENT ════════
  if (analysis && analysis.win_rate_assessment) {
    sectionTitle('Win Rate Assessment', [30, 64, 175]);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(51, 65, 85);
    y = drawWrappedText(pdf, analysis.win_rate_assessment, margin, y, contentW, 5, 30);
    y += 4;
  }

  // ════════ KEY METRICS ════════
  if (metrics) {
    sectionTitle('Key Metrics', [59, 130, 246]);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.setTextColor(30, 64, 175);
    pdf.text(`Total Deals: ${metrics.total_deals || 0}`, margin + 2, y);
    y += 6;
    pdf.setTextColor(16, 185, 129);
    pdf.text(`Won: ${metrics.win_count || 0} (${metrics.win_pct || '0%'})`, margin + 2, y);
    y += 6;
    pdf.setTextColor(239, 68, 68);
    pdf.text(`Lost: ${metrics.loss_count || 0} (${metrics.loss_pct || '0%'})`, margin + 2, y);
    y += 8;

    if (metrics.account_analytics) {
      const aa = metrics.account_analytics;
      pdf.setTextColor(30, 64, 175);
      pdf.text(`Unique Accounts: ${aa.unique_accounts || 0}`, margin + 2, y);
      y += 6;
      pdf.text(`Repeat Clients: ${aa.repeat_clients_count || 0}`, margin + 2, y);
      y += 6;
      if (aa.top_client_by_volume) {
        pdf.text(`Top Client: ${aa.top_client_by_volume} (${aa.top_client_volume_share || '0%'})`, margin + 2, y);
        y += 6;
      }
      y += 4;
    }
  }

  // ════════ KEY TAKEAWAYS ════════
  if (analysis && analysis.key_takeaways && analysis.key_takeaways.length > 0) {
    sectionTitle('Key Takeaways', [30, 64, 175]);
    analysis.key_takeaways.forEach((item) => {
      bulletItem(item);
    });
    y += 2;
  }

  // ════════ STRATEGIC RECOMMENDATIONS ════════
  if (analysis && analysis.strategic_recommendations && analysis.strategic_recommendations.length > 0) {
    sectionTitle('Strategic Recommendations', [6, 95, 70]);
    analysis.strategic_recommendations.forEach((item) => {
      bulletItem(item);
    });
    y += 2;
  }

  // ════════ DATA LIMITATIONS ════════
  if (analysis && analysis.data_limitations_noted && analysis.data_limitations_noted.length > 0) {
    sectionTitle('Data Limitations Noted', [180, 83, 9]);
    analysis.data_limitations_noted.forEach((item) => {
      bulletItem(item);
    });
    y += 2;
  }

  // ── Footer ──
  const pageCount = pdf.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    pdf.setFontSize(7);
    pdf.setTextColor(148, 163, 184);
    pdf.text(`AI Strategic Plan  •  Generated ${new Date().toLocaleDateString()}  •  Page ${i} of ${pageCount}`, margin, 292);
  }

  pdf.save(`AI_Strategic_Plan_${new Date().toISOString().split('T')[0]}.pdf`);
}

// ────────────────────────────────────────────────────────────────────
function StrategicPlan() {
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const quoteIds = location.state?.quoteIds?.length ? location.state.quoteIds : [];
  const quoteIdsKey = JSON.stringify(quoteIds);
  const prevQuoteIdsKey = useRef(null);

  const fetchStrategicPlan = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(
        `${API_URL}/ai-reports/historical-summary`,
        { quote_ids: quoteIds },
        { headers }
      );
      setData(res.data);
    } catch (err) {
      console.error('Strategic Plan fetch error:', err);
      setError(err.response?.data?.message || 'Failed to load strategic plan data. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [token, quoteIdsKey]);

  useEffect(() => {
    if (prevQuoteIdsKey.current !== quoteIdsKey) {
      prevQuoteIdsKey.current = quoteIdsKey;
      fetchStrategicPlan();
    }
  }, [quoteIdsKey, fetchStrategicPlan]);

  const handleExportPdf = () => {
    if (exportBtnRef.current) exportBtnRef.current.style.display = 'none';
    generatePdf(data, quoteIds);
    setTimeout(() => {
      if (exportBtnRef.current) exportBtnRef.current.style.display = '';
    }, 500);
  };

  const exportBtnRef = useRef(null);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '70vh', gap: 3, bgcolor: '#f8fafc' }}>
        <Avatar sx={{ bgcolor: '#3b82f6', color: 'white', width: 80, height: 80, boxShadow: '0 4px 14px rgba(59,130,246,0.3)' }}>
          <PsychologyIcon sx={{ fontSize: 40 }} />
        </Avatar>
        <Typography variant="h5" fontWeight="700" color="#1e293b">Analyzing Quote Performance...</Typography>
        <Typography variant="body1" color="#64748b">Crunching historical data to generate your AI-powered strategic plan</Typography>
        <Box sx={{ width: 300, maxWidth: '80%' }}>
          <LinearProgress sx={{ borderRadius: 2, height: 6, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: '#3b82f6' } }} />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, maxWidth: 900, mx: 'auto', mt: 8 }}>
        <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #fecaca', bgcolor: '#fef2f2', p: 4, textAlign: 'center' }}>
          <WarningAmberIcon sx={{ fontSize: 48, color: '#ef4444', mb: 2 }} />
          <Typography fontWeight="700" color="#b91c1c" mb={2}>Error Loading Strategic Plan</Typography>
          <Typography variant="body2" color="#ef4444" mb={3}>{error}</Typography>
          <Button variant="outlined" color="error" onClick={fetchStrategicPlan} sx={{ borderRadius: 2, fontWeight: 'bold' }}>
            Retry
          </Button>
        </Card>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}
          sx={{ mt: 3, borderRadius: 2, fontWeight: 'bold', color: '#3b82f6', '&:hover': { bgcolor: '#eff6ff' } }}>
          Go Back
        </Button>
      </Box>
    );
  }

  if (!data || !data.metrics) {
    return (
      <Box sx={{ p: 4, maxWidth: 900, mx: 'auto', mt: 8 }}>
        <Card elevation={0} sx={{ p: 8, textAlign: 'center', bgcolor: '#f8fafc', borderRadius: 4, border: '2px dashed #cbd5e1' }}>
          <PsychologyIcon sx={{ fontSize: 64, color: '#93c5fd', mb: 2 }} />
          <Typography variant="h6" color="#334155" fontWeight="700" gutterBottom>No Strategic Plan Data Available</Typography>
          <Typography variant="body2" color="#64748b" sx={{ mb: 3 }}>Try selecting a different set of quotes with historical activity.</Typography>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ borderRadius: 2, fontWeight: 'bold', px: 4, borderWidth: 2, '&:hover': { borderWidth: 2 } }}>
            Go Back
          </Button>
        </Card>
      </Box>
    );
  }

  const { analysis, metrics } = data;
  const accountAnalytics = metrics?.account_analytics || {};

  return (
    <Box sx={{ width: '100%', bgcolor: '#f8fafc', minHeight: '100vh', pb: 8 }}>

      {/* Header — matching QuoteDetails page style */}
      <Grow in={true} timeout={500}>
        <Box sx={{
          mb: 4, mt: 2, mx: { xs: 1, md: 3 }, p: 4, borderRadius: 4,
          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
          color: 'white',
          boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)',
          position: 'relative', overflow: 'hidden',
          display: 'flex', flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 3
        }}>
          <Box sx={{ position: 'relative', zIndex: 2 }}>
            <Typography variant="h4" fontWeight="800" sx={{ mb: 1, letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 2, color: 'white' }}>
              <PsychologyIcon sx={{ fontSize: 40, color: '#90caf9' }} />
              AI Strategic Plan
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
              Historical performance analysis & AI-generated insights for selected quotes
            </Typography>
            {quoteIds.length > 0 && (
              <Chip label={`${quoteIds.length} quote${quoteIds.length > 1 ? 's' : ''} analyzed`} size="small"
                sx={{ mt: 1.5, bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600, borderRadius: 1.5 }} />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, position: 'relative', zIndex: 2 }}>
            <Button ref={exportBtnRef} variant="contained" startIcon={<PictureAsPdfIcon />} onClick={handleExportPdf}
              sx={{
                borderRadius: 2, fontWeight: 'bold', px: 3, py: 1,
                bgcolor: 'white', color: '#1e3c72',
                boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
                '&:hover': { bgcolor: '#f0f0f0', transform: 'translateY(-2px)' },
                transition: 'all 0.2s'
              }}>
              Export PDF
            </Button>
            <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2, fontWeight: 'bold', px: 3, py: 1,
                borderColor: 'rgba(255,255,255,0.5)', color: 'white',
                bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.25)', borderColor: 'white' }
              }}>
              Back to Quotes
            </Button>
          </Box>
          <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
          <Box sx={{ position: 'absolute', bottom: -100, right: 100, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
        </Box>
      </Grow>

      <Box sx={{ px: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>

        {/* Win Rate Assessment */}
        {analysis && analysis.win_rate_assessment && (
          <Grow in={true} timeout={700}>
            <Card elevation={0} sx={{
              borderRadius: 4, border: '1px solid #bfdbfe', bgcolor: 'white', overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(59,130,246,0.08)'
            }}>
              <Box sx={{
                p: 2.5, background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                display: 'flex', alignItems: 'center', gap: 1.5
              }}>
                <Avatar sx={{ bgcolor: '#3b82f6', color: 'white', width: 40, height: 40 }}>
                  <AssessmentIcon sx={{ fontSize: 20 }} />
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" fontWeight="800" color="#1e40af" textTransform="uppercase" letterSpacing={0.5}>
                    Win Rate Assessment
                  </Typography>
                </Box>
              </Box>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="body2" sx={{ color: '#1e3a5f', lineHeight: 1.7, fontWeight: 500 }}>
                  {analysis.win_rate_assessment}
                </Typography>
              </CardContent>
            </Card>
          </Grow>
        )}

        {/* Deal Performance & Account Analytics */}
        <Grid container spacing={3}>
          {/* Deal Performance Card */}
          <Grid item xs={12} md={6}>
            <Grow in={true} timeout={800}>
              <Card elevation={0} sx={{
                height: '100%', borderRadius: 4, border: '1px solid #d1fae5',
                bgcolor: '#f0fdf4', overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(16,185,129,0.06)'
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Avatar sx={{ bgcolor: '#10b981', color: 'white', width: 40, height: 40 }}>
                      <TrendingUpIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="800" color="#065f46" textTransform="uppercase" letterSpacing={0.5}>
                        Deal Performance
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-around', textAlign: 'center', mb: 3 }}>
                    <Box>
                      <Typography variant="h4" fontWeight="900" color="#1e293b">{metrics.total_deals || 0}</Typography>
                      <Typography variant="caption" color="#64748b" fontWeight="600">Total Deals</Typography>
                    </Box>
                    <Box>
                      <Typography variant="h4" fontWeight="900" color="#10b981">{metrics.win_count || 0}</Typography>
                      <Typography variant="caption" color="#64748b" fontWeight="600">Won ({metrics.win_pct || '0%'})</Typography>
                    </Box>
                    <Box>
                      <Typography variant="h4" fontWeight="900" color="#ef4444">{metrics.loss_count || 0}</Typography>
                      <Typography variant="caption" color="#64748b" fontWeight="600">Lost ({metrics.loss_pct || '0%'})</Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" color="#065f46" fontWeight="600">Win Rate</Typography>
                      <Typography variant="caption" color="#065f46" fontWeight="800">{metrics.win_pct || '0%'}</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={parseFloat(metrics.win_pct) || 0}
                      sx={{
                        height: 10, borderRadius: 5, bgcolor: '#d1fae5',
                        '& .MuiLinearProgress-bar': { bgcolor: '#10b981', borderRadius: 5 }
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grow>
          </Grid>

          {/* Account Analytics Card */}
          <Grid item xs={12} md={6}>
            <Grow in={true} timeout={900}>
              <Card elevation={0} sx={{
                height: '100%', borderRadius: 4, border: '1px solid #dbeafe',
                bgcolor: '#eff6ff', overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(59,130,246,0.06)'
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Avatar sx={{ bgcolor: '#3b82f6', color: 'white', width: 40, height: 40 }}>
                      <AccountBalanceIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="800" color="#1e40af" textTransform="uppercase" letterSpacing={0.5}>
                        Account Analytics
                      </Typography>
                      {analysis?.account_diversity_score && (
                        <Chip label={`Diversity: ${analysis.account_diversity_score}`} size="small"
                          sx={{ mt: 0.5, bgcolor: '#3b82f6', color: 'white', fontWeight: 600, borderRadius: 1.5, height: 22, fontSize: '0.7rem' }} />
                      )}
                    </Box>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Card elevation={0} sx={{ textAlign: 'center', p: 2, borderRadius: 3, bgcolor: 'white', border: '1px solid #bfdbfe' }}>
                        <Typography variant="h4" fontWeight="900" color="#1e40af">{accountAnalytics.unique_accounts || 0}</Typography>
                        <Typography variant="caption" color="#64748b" fontWeight="600">Unique Accounts</Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={6}>
                      <Card elevation={0} sx={{ textAlign: 'center', p: 2, borderRadius: 3, bgcolor: 'white', border: '1px solid #bfdbfe' }}>
                        <Typography variant="h4" fontWeight="900" color="#10b981">{accountAnalytics.repeat_clients_count || 0}</Typography>
                        <Typography variant="caption" color="#64748b" fontWeight="600">Repeat Clients</Typography>
                      </Card>
                    </Grid>
                    {accountAnalytics.top_client_by_volume && (
                      <Grid item xs={12}>
                        <Card elevation={0} sx={{ p: 2, borderRadius: 3, bgcolor: 'white', border: '1px solid #fef3c7', borderLeft: '4px solid #f59e0b' }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            <Avatar sx={{ bgcolor: '#f59e0b', color: 'white', width: 32, height: 32, flexShrink: 0 }}>
                              <EmojiEventsIcon sx={{ fontSize: 18 }} />
                            </Avatar>
                            <Box>
                              <Typography variant="caption" fontWeight="800" color="#b45309" textTransform="uppercase" letterSpacing={0.5}>
                                Top Client by Volume
                              </Typography>
                              <Typography variant="body2" fontWeight="700" color="#1e293b" mt={0.5}>
                                {accountAnalytics.top_client_by_volume}
                              </Typography>
                              <Typography variant="caption" color="#64748b">
                                {accountAnalytics.top_client_volume_share || '0%'} of total volume
                              </Typography>
                            </Box>
                          </Box>
                        </Card>
                      </Grid>
                    )}
                    {accountAnalytics.summary_note && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="#64748b" sx={{ fontStyle: 'italic', fontSize: '0.78rem', px: 0.5 }}>
                          {accountAnalytics.summary_note}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grow>
          </Grid>
        </Grid>

        {/* Key Takeaways & Strategic Recommendations */}
        <Grid container spacing={3}>
          {/* Key Takeaways */}
          <Grid item xs={12} md={6}>
            <Grow in={true} timeout={1000}>
              <Card elevation={0} sx={{
                height: '100%', borderRadius: 4, border: '1px solid #bfdbfe', bgcolor: 'white',
                boxShadow: '0 4px 20px rgba(0,0,0,0.03)', overflow: 'hidden'
              }}>
                <Box sx={{
                  p: 2.5, background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                  display: 'flex', alignItems: 'center', gap: 1.5
                }}>
                  <Avatar sx={{ bgcolor: '#3b82f6', color: 'white', width: 40, height: 40 }}>
                    <LightbulbIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight="800" color="#1e40af" textTransform="uppercase" letterSpacing={0.5}>
                      Key Takeaways
                    </Typography>
                  </Box>
                  <Chip label={`${(analysis?.key_takeaways || []).length} insights`} size="small"
                    sx={{ ml: 'auto', bgcolor: '#3b82f6', color: 'white', fontWeight: 600, borderRadius: 1.5, height: 22, fontSize: '0.7rem' }} />
                </Box>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {(analysis?.key_takeaways || []).map((item, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <Avatar sx={{ bgcolor: '#eff6ff', color: '#3b82f6', width: 28, height: 28, fontSize: '0.75rem', fontWeight: 800, flexShrink: 0 }}>
                          {idx + 1}
                        </Avatar>
                        <Typography variant="body2" sx={{ color: '#1e3a5f', lineHeight: 1.6, pt: 0.3, fontSize: '0.83rem' }}>
                          {item}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  {(!analysis?.key_takeaways || analysis.key_takeaways.length === 0) && (
                    <Typography variant="body2" color="#94a3b8" textAlign="center" py={3}>No key takeaways available.</Typography>
                  )}
                </CardContent>
              </Card>
            </Grow>
          </Grid>

          {/* Strategic Recommendations */}
          <Grid item xs={12} md={6}>
            <Grow in={true} timeout={1100}>
              <Card elevation={0} sx={{
                height: '100%', borderRadius: 4, border: '1px solid #d1fae5', bgcolor: 'white',
                boxShadow: '0 4px 20px rgba(0,0,0,0.03)', overflow: 'hidden'
              }}>
                <Box sx={{
                  p: 2.5, background: 'linear-gradient(135deg, #f0fdf4 0%, #d1fae5 100%)',
                  display: 'flex', alignItems: 'center', gap: 1.5
                }}>
                  <Avatar sx={{ bgcolor: '#10b981', color: 'white', width: 40, height: 40 }}>
                    <CheckCircleIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight="800" color="#065f46" textTransform="uppercase" letterSpacing={0.5}>
                      Strategic Recommendations
                    </Typography>
                  </Box>
                  <Chip label={`${(analysis?.strategic_recommendations || []).length} actions`} size="small"
                    sx={{ ml: 'auto', bgcolor: '#10b981', color: 'white', fontWeight: 600, borderRadius: 1.5, height: 22, fontSize: '0.7rem' }} />
                </Box>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {(analysis?.strategic_recommendations || []).map((item, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <Avatar sx={{ bgcolor: '#f0fdf4', color: '#10b981', width: 28, height: 28, flexShrink: 0 }}>
                          <LightbulbIcon sx={{ fontSize: 16 }} />
                        </Avatar>
                        <Typography variant="body2" sx={{ color: '#064e3b', lineHeight: 1.6, pt: 0.3, fontSize: '0.83rem' }}>
                          {item}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  {(!analysis?.strategic_recommendations || analysis.strategic_recommendations.length === 0) && (
                    <Typography variant="body2" color="#94a3b8" textAlign="center" py={3}>No strategic recommendations available.</Typography>
                  )}
                </CardContent>
              </Card>
            </Grow>
          </Grid>
        </Grid>

        {/* Data Limitations */}
        {analysis && analysis.data_limitations_noted && analysis.data_limitations_noted.length > 0 && (
          <Grow in={true} timeout={1200}>
            <Card elevation={0} sx={{
              borderRadius: 4, border: '1px solid #fef3c7', bgcolor: '#fffbeb', overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(245,158,11,0.06)'
            }}>
              <Box sx={{
                p: 2.5, background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                display: 'flex', alignItems: 'center', gap: 1.5
              }}>
                <Avatar sx={{ bgcolor: '#f59e0b', color: 'white', width: 40, height: 40 }}>
                  <ErrorOutlineIcon sx={{ fontSize: 20 }} />
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" fontWeight="800" color="#b45309" textTransform="uppercase" letterSpacing={0.5}>
                    Data Limitations Noted
                  </Typography>
                </Box>
                <Chip label={`${analysis.data_limitations_noted.length} noted`} size="small"
                  sx={{ ml: 'auto', bgcolor: '#f59e0b', color: 'white', fontWeight: 600, borderRadius: 1.5, height: 22, fontSize: '0.7rem' }} />
              </Box>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {analysis.data_limitations_noted.map((item, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <WarningAmberIcon sx={{ fontSize: 18, color: '#f59e0b', flexShrink: 0, mt: 0.2 }} />
                      <Typography variant="body2" color="#78350f" sx={{ fontSize: '0.82rem', fontStyle: 'italic', fontWeight: 500 }}>
                        {item}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grow>
        )}

        {/* Refresh / Status Indicator */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Chip
            icon={<LoopIcon sx={{ fontSize: 14 }} />}
            label={`Status: ${data?.status || 'Ready'}`}
            size="small"
            sx={{
              bgcolor: '#f1f5f9', color: '#64748b', fontWeight: 600, borderRadius: 2,
              '& .MuiChip-icon': { color: '#3b82f6' }
            }}
          />
        </Box>

      </Box>
    </Box>
  );
}

export default StrategicPlan;